import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trash2, UserPlus, Shield, User as UserIcon, UserCheck } from "lucide-react";
import { SmmAccessGate } from "@/components/crm/SmmAccessGate";
import {
  listSmmMembers,
  addSmmMember,
  updateSmmMemberRole,
  removeSmmMember,
  listSmmCandidates,
  addSmmMemberById,
  type SmmRole,
} from "@/lib/smm-access.functions";

export const Route = createFileRoute("/crm/smm/members")({
  component: () => (
    <SmmAccessGate require="manage">{() => <MembersPage />}</SmmAccessGate>
  ),
});

export function MembersPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listSmmMembers);
  const addFn = useServerFn(addSmmMember);
  const updateFn = useServerFn(updateSmmMemberRole);
  const removeFn = useServerFn(removeSmmMember);
  const candidatesFn = useServerFn(listSmmCandidates);
  const addByIdFn = useServerFn(addSmmMemberById);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["smm-members"],
    queryFn: () => listFn(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: candidates = [], isLoading: candLoading } = useQuery({
    queryKey: ["smm-candidates"],
    queryFn: () => candidatesFn(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SmmRole>("workplace");
  const [err, setErr] = useState<string | null>(null);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["smm-members"] });
    qc.invalidateQueries({ queryKey: ["smm-candidates"] });
  };

  const add = useMutation({
    mutationFn: (input: { email: string; role: SmmRole }) => addFn({ data: input }),
    onSuccess: () => {
      setEmail("");
      setErr(null);
      invalidateAll();
    },
    onError: (e: any) => setErr(e?.message ?? "Failed to add member"),
  });

  const grant = useMutation({
    mutationFn: (input: { user_id: string; role: SmmRole }) => addByIdFn({ data: input }),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: (input: { user_id: string; role: SmmRole }) => updateFn({ data: input }),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (input: { user_id: string }) => removeFn({ data: input }),
    onSuccess: invalidateAll,
  });

  return (
    <div className="space-y-3 text-[#212529]">
      <section className="bg-white rounded-xl border p-4" style={{ borderColor: "#eceff3" }}>
        <div className="flex items-center gap-2 mb-1">
          <UserPlus className="size-3.5 text-[#0d6efd]" strokeWidth={1.85} />
          <h2 className="text-xs font-semibold uppercase tracking-wide text-[#6c757d]">Add member</h2>
        </div>
        <p className="text-xs text-[#6c757d]">Must already have a platform account.</p>
        <form
          className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!email.trim()) return;
            add.mutate({ email: email.trim(), role });
          }}
        >
          <input
            type="email"
            required
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/25"
            style={{ borderColor: "#dee2e6" }}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as SmmRole)}
            className="h-9 px-3 rounded-lg border text-sm bg-white"
            style={{ borderColor: "#dee2e6" }}
          >
            <option value="workplace">Workplace</option>
            <option value="smm_admin">SMM Admin</option>
          </select>
          <button
            type="submit"
            disabled={add.isPending}
            className="inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg bg-[#0d6efd] text-white text-sm font-semibold hover:bg-[#0b5ed7] disabled:opacity-60"
          >
            <UserPlus className="size-3.5" /> {add.isPending ? "Adding…" : "Add"}
          </button>
        </form>
        {err && <div className="mt-2 text-xs text-[#dc3545]">{err}</div>}
      </section>

      <section className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#eceff3" }}>
        <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "#eceff3" }}>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[#6c757d]">Quick grant</h2>
            <p className="text-[11px] text-[#6c757d] mt-0.5">Admins & CRM users without SMM access.</p>
          </div>
          <span className="text-xs text-[#6c757d]">{candidates.length}</span>
        </div>
        {candLoading ? (
          <div className="p-8 text-center text-sm text-[#6c757d]">Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6c757d]">Everyone with admin or CRM access is already a member.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "#eceff3" }}>
            {candidates.map((c) => {
              const pending = grant.isPending && grant.variables?.user_id === c.user_id;
              return (
                <li key={c.user_id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="size-8 rounded-md bg-[#e7f1ff] text-[#0d6efd] grid place-items-center text-[11px] font-bold">
                    {(c.display_name ?? c.email ?? "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#212529] truncate">
                      {c.display_name || c.email || c.user_id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-[#6c757d] truncate flex items-center gap-1.5 flex-wrap">
                      <span>{c.email ?? "—"}</span>
                      {c.roles.map((r) => (
                        <span key={r} className="px-1.5 py-0.5 rounded bg-[#f8f9fa] text-[10px] font-semibold uppercase tracking-wider text-[#6c757d]">
                          {r.replace("_", " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => grant.mutate({ user_id: c.user_id, role: "workplace" })}
                    disabled={pending}
                    className="px-2.5 h-8 rounded-lg border bg-white text-xs font-semibold hover:bg-[#f8f9fa] disabled:opacity-50"
                    style={{ borderColor: "#dee2e6" }}
                  >
                    Workplace
                  </button>
                  <button
                    onClick={() => grant.mutate({ user_id: c.user_id, role: "smm_admin" })}
                    disabled={pending}
                    className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg bg-[#0d6efd] text-white text-xs font-semibold hover:bg-[#0b5ed7] disabled:opacity-50"
                  >
                    <UserCheck className="size-3.5" /> Admin
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#eceff3" }}>
        <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "#eceff3" }}>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-[#6c757d]">Members</h2>
          <span className="text-xs text-[#6c757d]">{members.length} total</span>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-[#6c757d]">Loading…</div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6c757d]">No members yet. Add the first one above.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "#eceff3" }}>
            {members.map((m) => (
              <li key={m.user_id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="size-8 rounded-md bg-[#e7f1ff] text-[#0d6efd] grid place-items-center">
                  {m.role === "smm_admin" ? <Shield className="size-3.5" strokeWidth={1.85} /> : <UserIcon className="size-3.5" strokeWidth={1.85} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#212529] truncate">
                    {m.display_name || m.email || m.user_id.slice(0, 8)}
                  </div>
                  <div className="text-xs text-[#6c757d] truncate">{m.email ?? "—"}</div>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => update.mutate({ user_id: m.user_id, role: e.target.value as SmmRole })}
                  className="h-8 px-2 rounded-lg border text-xs bg-white"
                  style={{ borderColor: "#dee2e6" }}
                >
                  <option value="workplace">Workplace</option>
                  <option value="smm_admin">SMM Admin</option>
                </select>
                <button
                  onClick={() => {
                    if (confirm(`Remove ${m.email ?? "this user"} from SMM?`)) {
                      remove.mutate({ user_id: m.user_id });
                    }
                  }}
                  className="size-8 grid place-items-center rounded-lg text-[#6c757d] hover:text-[#dc3545] hover:bg-[#f8d7da]"
                  aria-label="Remove"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
